import { z } from 'zod';

/**
 * Agency (tenant) vocabulary — RBAC Phase R.3b-1 tenancy FOUNDATION (#315).
 *
 * Mirrors the IMS-role + operator-action verb anti-clobber pattern (Task #90 /
 * migration 0045; roles = migration 0083): the canonical list is THIS const-tuple;
 * the DB enforces it via an FK from `agencies.kind` into the `valid_agency_kinds`
 * lookup table (migration 0084) — NOT a CHECK enum, so parallel branches can extend
 * the vocabulary with ONE idempotent INSERT row instead of colliding on a constraint
 * rewrite. A new kind = one literal here + one seed INSERT in a migration + schema.ts
 * lockstep, all pinned together by `agency.test.ts`.
 *
 * `kind` classifies a tenant — the coarse type an EOC cares about when reasoning
 * about who owns what:
 *   - federal        — a national department / agency (e.g. PS, ECCC, DND).
 *   - provincial     — a province / territory EMO (e.g. EMBC, AEMA).
 *   - municipal      — a city / regional-district local government.
 *   - first_nation   — a First Nation / band-council government. UN-FOLDED from
 *                      'municipal' by the W3 spine (migration 0120) so a band council
 *                      is a first-class LEAD ORGANIZATION for incident command. The
 *                      canonical jurisdiction tier is {municipal, provincial,
 *                      first_nation, federal}; 'agency' / 'drone-operator' are
 *                      non-jurisdiction classes.
 *   - agency         — any other operating partner (utility, NGO, mutual-aid, …).
 *   - drone-operator — an EXTERNAL drone-marketplace operator org (Drone D1 #316).
 *                      A distinct TENANT CLASS, not a jurisdiction tier: its users
 *                      get the `drone-operator` portal role and are walled out of the
 *                      COP by the default-deny `roleCanAccessCop` boundary (see
 *                      ./user-role). Added in migration 0088. The COP access boundary
 *                      keys on the user ROLE, not on this kind — the kind classifies
 *                      the org, the role gates the principal.
 *
 * R.3b-1 is FOUNDATION ONLY — this ships the agency model + a per-user agency link +
 * the immutable-audit FK + the identity seam. There is NO per-agency row-scoping yet
 * (no WHERE filters, no agency_id on operational tables) and AUTH_ENFORCED is NOT
 * flipped — that is all R.3b-2. This phase changes NO read/write behavior.
 */
export const AGENCY_KINDS = [
  'federal',
  'provincial',
  'municipal',
  'first_nation',
  'agency',
  'drone-operator',
] as const;

export const AgencyKindSchema = z.enum(AGENCY_KINDS);
export type AgencyKind = z.infer<typeof AgencyKindSchema>;

/**
 * The deterministic, seeded default-agency UUID (migration 0084 seeds exactly this
 * row). It is the single-tenant anchor used until R.3b-2 introduces real per-agency
 * scoping:
 *   - the `users.agency_id` column DEFAULT (so every existing/demo user backfills to
 *     it and create-user's omitted `--agency` falls back to it);
 *   - the demo / dev identity's `agencyId` when AUTH_ENFORCED is OFF (so an
 *     un-authenticated COP keeps a stable, valid tenant).
 * Pinned to the migration seed + the schema.ts column DEFAULT by `agency.test.ts`.
 */
export const DEFAULT_AGENCY_ID = '00000000-0000-0000-0000-000000000001';

/** The seeded default agency's display name. RBAC Phase 1 (#566, migration 0137) RENAMED
 *  this from "Default Agency" to the province's operator-facing name — it IS the
 *  designated PEOC (`agencies.is_peoc`). The UUID (DEFAULT_AGENCY_ID) is unchanged. This
 *  is the ONE-LINE-VETO constant: a founder name change here must move the 0084 seed
 *  literal in lockstep (pinned by agency.test.ts) + a data UPDATE on the live row. */
export const DEFAULT_AGENCY_NAME = 'Provincial Emergency Operations Centre (PEOC)';

/** The seeded default agency's kind (migration 0084). Member of AGENCY_KINDS. */
export const DEFAULT_AGENCY_KIND: AgencyKind = 'provincial';

/**
 * Coerce an arbitrary stored / wire value into an `AgencyKind`, returning `null` on
 * any unexpected value (rather than silently defaulting to a tier). The
 * `agencies.kind` FK already guarantees a valid value at rest; this guards read paths
 * that surface a kind to clients.
 */
export function parseAgencyKind(value: unknown): AgencyKind | null {
  const parsed = AgencyKindSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * The agency kinds that are valid DOWN-TASK (`agency_tasks`) counterparties — every
 * AGENCY_KINDS member EXCEPT `drone-operator`. A drone-operator org is reached through the
 * drone MARKETPLACE (`drone_missions`), never an agency_task, so it must not appear in the
 * "Task agency" picker (W4 coordination council §D G-3 — the over-broad-picker finding /
 * W4d #519). Every jurisdiction tier (federal / provincial / municipal / first_nation) plus
 * the generic `agency` operating partner IS a valid counterparty — App A #4/#82
 * (assisting/supporting), #44 (federal/jurisdiction), #52 (mutual assistance). Backend
 * taxonomy only (never an operator-facing label); GET /api/agencies?kind=… filters by it.
 */
export const DOWN_TASK_TARGET_KINDS = AGENCY_KINDS.filter(
  (k): k is Exclude<AgencyKind, 'drone-operator'> => k !== 'drone-operator',
);

/** True when an agency kind is a valid down-task counterparty (i.e. not a drone-operator). */
export function isDownTaskTargetKind(kind: string): boolean {
  return (DOWN_TASK_TARGET_KINDS as readonly string[]).includes(kind);
}
